import React, { useState, useCallback, useRef, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';

import BOMItemNode from './nodes/BOMItemNode';
import BOMEdge from './edges/BOMEdge';
import ItemSearchPanel from './panels/ItemSearchPanel';
import LoadBOMModal from './panels/LoadBOMModal';
import SaveConfirmModal from './panels/SaveConfirmModal';
import CreateItemModal from './panels/CreateItemModal';
import EditItemPanel from './panels/EditItemPanel';
import SaveAsNewModal from './panels/SaveAsNewModal';
import { useBOMOperations } from './hooks/useBOMOperations';
import { layoutBOM } from './utils/layoutBOM';
import { calculateBOMDiff, getModifiedParents } from './utils/bomDiff';
import { validateBOMConnectivity, wouldCreateCycle } from './utils/validateBOM';
import { useAuth } from '../../contexts/AuthContext';

import './BOMConfigurator.css';

const nodeTypes = {
  bomItem: BOMItemNode,
};

const edgeTypes = {
  bomEdge: BOMEdge,
};

const BOMConfigurator = () => {
  const { user } = useAuth();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalState, setOriginalState] = useState({ nodes: [], edges: [] });
  const [isSaving, setIsSaving] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showSaveAsNewModal, setShowSaveAsNewModal] = useState(false);
  
  // PENDING ITEMS - new items to be created on save
  const [pendingItems, setPendingItems] = useState([]);
  
  // MODIFIED ITEMS - existing items that have been edited (keyed by itemId)
  const [modifiedItems, setModifiedItems] = useState({});
  
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const { loadBOMTree, saveBOMChanges, loading, error } = useBOMOperations();

  // Get pending item codes for validation
  const pendingItemCodes = useMemo(() => {
    return pendingItems.map(item => item.item_code);
  }, [pendingItems]);

  // Calculate top-level item(s) - nodes with no parent (never appear as edge.source)
  const topLevelItems = useMemo(() => {
    if (nodes.length === 0) return [];
    if (edges.length === 0) return nodes;
    
    const childNodeIds = new Set(edges.map(e => e.source));
    return nodes.filter(n => !childNodeIds.has(n.id));
  }, [nodes, edges]);

  // The primary top-level item (for display purposes)
  const topLevelItem = useMemo(() => {
    return topLevelItems.length === 1 ? topLevelItems[0] : null;
  }, [topLevelItems]);

  // Calculate which nodes have children (are sub-assemblies)
  const nodesWithChildrenIds = useMemo(() => {
    const parentIds = new Set();
    edges.forEach(edge => {
      parentIds.add(edge.target);
    });
    return parentIds;
  }, [edges]);

  // Calculate diff and modified parents
  const { diff, modifiedParents, modifiedParentIds } = useMemo(() => {
    // Don't calculate if no unsaved changes flag
    if (!hasUnsavedChanges) {
      return { diff: null, modifiedParents: [], modifiedParentIds: new Set() };
    }
    
    // Don't calculate if original state is empty and we have no edges
    if (originalState.nodes.length === 0 && edges.length === 0) {
      return { diff: null, modifiedParents: [], modifiedParentIds: new Set() };
    }
    
    const calculatedDiff = calculateBOMDiff(originalState, { nodes, edges });
    const topLevelId = topLevelItem?.data?.itemId || null;
    const parents = getModifiedParents(calculatedDiff, topLevelId);
    const parentIds = new Set(parents.map(p => p.parentId));
    
    return { 
      diff: calculatedDiff, 
      modifiedParents: parents,
      modifiedParentIds: parentIds,
    };
  }, [originalState, nodes, edges, hasUnsavedChanges, topLevelItem]);

  // Count of modified existing items
  const modifiedItemsCount = useMemo(() => {
    return Object.keys(modifiedItems).length;
  }, [modifiedItems]);

  // Update nodes with modification status, children status, and top-level status
  const nodesWithStatus = useMemo(() => {
    // Calculate children count per node
    const childrenCountMap = new Map();
    edges.forEach(edge => {
      const count = childrenCountMap.get(edge.target) || 0;
      childrenCountMap.set(edge.target, count + 1);
    });

    // Get top-level node IDs
    const topLevelNodeIds = new Set(topLevelItems.map(n => n.id));

    return nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        hasModifiedBOM: modifiedParentIds.has(node.data.itemId),
        hasChildren: nodesWithChildrenIds.has(node.id),
        childrenCount: childrenCountMap.get(node.id) || 0,
        isModified: !node.data.isPending && modifiedItems[node.data.itemId] !== undefined,
        isTopLevel: topLevelNodeIds.has(node.id) && edges.length > 0,
      },
    }));
  }, [nodes, edges, modifiedParentIds, nodesWithChildrenIds, modifiedItems, topLevelItems]);

  // Get the currently selected node object
  const selectedNodeObject = useMemo(() => {
    if (!selectedNode) return null;
    return nodesWithStatus.find(n => n.id === selectedNode) || null;
  }, [selectedNode, nodesWithStatus]);

  // Handle new connections
  const onConnect = useCallback((params) => {
    if (params.source === params.target) {
      alert('Cannot connect an item to itself');
      return;
    }

    if (wouldCreateCycle(edges, params.source, params.target)) {
      alert('Cannot create circular reference');
      return;
    }

    const existingEdge = edges.find(
      e => e.source === params.source && e.target === params.target
    );
    if (existingEdge) {
      alert('Connection already exists');
      return;
    }

    const sourceNode = nodes.find(n => n.id === params.source);
    const targetNode = nodes.find(n => n.id === params.target);
    
    if (sourceNode && targetNode) {
      const duplicateBOMEdge = edges.find(e => {
        const edgeSource = nodes.find(n => n.id === e.source);
        const edgeTarget = nodes.find(n => n.id === e.target);
        return edgeSource?.data?.itemId === sourceNode.data.itemId &&
               edgeTarget?.data?.itemId === targetNode.data.itemId;
      });
      
      if (duplicateBOMEdge) {
        alert(`${sourceNode.data.itemCode} is already a component of ${targetNode.data.itemCode}.\n\nYou cannot add the same item twice to the same parent.`);
        return;
      }
    }

    const newEdge = {
      ...params,
      id: `bom-${params.source}-${params.target}`,
      type: 'bomEdge',
      data: {
        quantity: 1,
        isPhantomPassthrough: false,
      },
    };
    
    setEdges((eds) => addEdge(newEdge, eds));
    setHasUnsavedChanges(true);
    setValidationErrors([]);
  }, [edges, nodes, setEdges]);

  // Handle dropping items from search panel
  const onDrop = useCallback(async (event) => {
    event.preventDefault();

    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const itemData = JSON.parse(event.dataTransfer.getData('application/bom-item'));

    if (!itemData || !reactFlowInstance) return;

    const position = reactFlowInstance.screenToFlowPosition({
      x: event.clientX - reactFlowBounds.left,
      y: event.clientY - reactFlowBounds.top,
    });

    const existingNode = nodes.find(n => n.data.itemId === itemData.id);
    
    if (existingNode) {
      const choice = window.confirm(
        `${itemData.item_code} is already on the canvas.\n\n` +
        `Click OK to add another instance (for connecting to a different parent).\n` +
        `Click Cancel to highlight the existing one.`
      );
      
      if (!choice) {
        setSelectedNode(existingNode.id);
        setSelectedEdge(null);
        
        reactFlowInstance.setCenter(
          existingNode.position.x + 100,
          existingNode.position.y + 40,
          { zoom: 1, duration: 500 }
        );
        
        setNodes(nds => nds.map(n => {
          if (n.id === existingNode.id) {
            return { ...n, data: { ...n.data, isHighlighted: true } };
          }
          return n;
        }));
        
        setTimeout(() => {
          setNodes(nds => nds.map(n => {
            if (n.id === existingNode.id) {
              return { ...n, data: { ...n.data, isHighlighted: false } };
            }
            return n;
          }));
        }, 1500);
        
        return;
      }
      
      const newNode = {
        id: `node-${itemData.id}-${Date.now()}`,
        type: 'bomItem',
        position,
        data: {
          itemId: itemData.id,
          itemCode: itemData.item_code,
          itemName: itemData.name,
          itemType: itemData.item_type,
          isPhantom: itemData.is_phantom,
          isPending: itemData.isPending || false,
          isNew: true,
          instanceOf: existingNode.id,
          description: itemData.description || '',
        },
      };

      setNodes((nds) => nds.concat(newNode));
      setHasUnsavedChanges(true);
      setValidationErrors([]);
      return;
    }

    if (itemData.isPending) {
      const newNode = {
        id: `node-${itemData.id}-${Date.now()}`,
        type: 'bomItem',
        position,
        data: {
          itemId: itemData.id,
          itemCode: itemData.item_code,
          itemName: itemData.name,
          itemType: itemData.item_type,
          isPhantom: itemData.is_phantom,
          isPending: true,
          isNew: true,
          description: itemData.description || '',
        },
      };

      setNodes((nds) => nds.concat(newNode));
      setHasUnsavedChanges(true);
      setValidationErrors([]);
      return;
    }

    try {
      const { nodes: bomNodes, edges: bomEdges } = await loadBOMTree(itemData.id);
      
      if (bomNodes.length > 1) {
        console.log(`Loading ${bomNodes.length} nodes and ${bomEdges.length} edges for ${itemData.item_code}`);
        
        const existingItemIds = new Set(nodes.map(n => n.data.itemId));
        const newNodes = bomNodes.filter(n => !existingItemIds.has(n.data.itemId));
        
        const itemIdToNodeId = new Map();
        nodes.forEach(n => itemIdToNodeId.set(n.data.itemId, n.id));
        newNodes.forEach(n => itemIdToNodeId.set(n.data.itemId, n.id));
        
        const remappedEdges = bomEdges.map(e => {
          const sourceNode = bomNodes.find(n => n.id === e.source);
          const targetNode = bomNodes.find(n => n.id === e.target);
          
          if (!sourceNode || !targetNode) return null;
          
          const newSourceId = itemIdToNodeId.get(sourceNode.data.itemId) || e.source;
          const newTargetId = itemIdToNodeId.get(targetNode.data.itemId) || e.target;
          
          return {
            ...e,
            id: `bom-${newSourceId}-${newTargetId}`,
            source: newSourceId,
            target: newTargetId,
          };
        }).filter(Boolean);
        
        const allNodeIds = new Set([
          ...nodes.map(n => n.id),
          ...newNodes.map(n => n.id)
        ]);
        
        const validNewEdges = remappedEdges.filter(e => {
          return allNodeIds.has(e.source) && allNodeIds.has(e.target);
        });

        const existingEdgeKeys = new Set(edges.map(e => `${e.source}-${e.target}`));
        const filteredNewEdges = validNewEdges.filter(e => {
          const key = `${e.source}-${e.target}`;
          return !existingEdgeKeys.has(key);
        });

        const markedNewNodes = newNodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            isNew: true,
          },
        }));

        const combinedNodes = [...nodes, ...markedNewNodes];
        const combinedEdges = [...edges, ...filteredNewEdges];

        const { nodes: layoutedNodes, edges: layoutedEdges } = layoutBOM(combinedNodes, combinedEdges);

        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
        
        // Update originalState to include loaded BOM relationships
        // so existing edges don't appear as "new" changes
        setOriginalState({
          nodes: layoutedNodes.map(n => ({ 
            ...n, 
            data: { ...n.data }, 
            position: { ...n.position } 
          })),
          edges: layoutedEdges.map(e => ({ 
            ...e, 
            data: { ...e.data } 
          })),
        });
        
        // Only mark unsaved if user actually needs to make connections
        // Loading an existing complete BOM = no changes needed
        setHasUnsavedChanges(false);
        setValidationErrors([]);
        
      } else {
        const newNode = {
          id: `node-${itemData.id}-${Date.now()}`,
          type: 'bomItem',
          position,
          data: {
            itemId: itemData.id,
            itemCode: itemData.item_code,
            itemName: itemData.name,
            itemType: itemData.item_type,
            isPhantom: itemData.is_phantom,
            isPending: false,
            isNew: true,
            description: itemData.description || '',
          },
        };

        setNodes((nds) => nds.concat(newNode));
        setHasUnsavedChanges(true);
        setValidationErrors([]);
      }
    } catch (err) {
      console.error('Failed to load item BOM:', err);
      
      const newNode = {
        id: `node-${itemData.id}-${Date.now()}`,
        type: 'bomItem',
        position,
        data: {
          itemId: itemData.id,
          itemCode: itemData.item_code,
          itemName: itemData.name,
          itemType: itemData.item_type,
          isPhantom: itemData.is_phantom,
          isPending: false,
          isNew: true,
          description: itemData.description || '',
        },
      };

      setNodes((nds) => nds.concat(newNode));
      setHasUnsavedChanges(true);
      setValidationErrors([]);
    }
  }, [reactFlowInstance, nodes, edges, setNodes, setEdges, loadBOMTree]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle creating a new item
  const handleCreateItem = useCallback((newItem) => {
    setPendingItems(prev => [...prev, newItem]);
    
    const newNode = {
      id: `node-${newItem.id}`,
      type: 'bomItem',
      position: { x: 200, y: 200 },
      data: {
        itemId: newItem.id,
        itemCode: newItem.item_code,
        itemName: newItem.name,
        itemType: newItem.item_type,
        isPhantom: newItem.is_phantom,
        isPending: true,
        isNew: true,
        description: newItem.description || '',
      },
    };

    setNodes((nds) => nds.concat(newNode));
    setHasUnsavedChanges(true);
    setValidationErrors([]);
  }, [setNodes]);

  // Handle updating node data (for editing items on canvas)
  const handleUpdateNode = useCallback((nodeId, updates) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    
    const isExistingItem = !node.data.isPending;
    const isCodeChanged = updates.itemCode !== undefined && 
                        updates.itemCode !== node.data.itemCode;
    
    // If changing the code of an existing item, convert it to a new pending item
    if (isExistingItem && isCodeChanged) {
        const newPendingId = `pending-${Date.now()}`;
        const newNodeId = `node-${newPendingId}`;
        const oldNodeId = node.id;
        const oldItemId = node.data.itemId;
        
        // Create new pending item
        const newPendingItem = {
        id: newPendingId,
        item_code: updates.itemCode,
        name: updates.itemName !== undefined ? updates.itemName : node.data.itemName,
        description: updates.description !== undefined ? updates.description : (node.data.description || ''),
        item_type: updates.itemType !== undefined ? updates.itemType : node.data.itemType,
        is_phantom: updates.isPhantom !== undefined ? updates.isPhantom : node.data.isPhantom,
        isPending: true,
        };
        
        setPendingItems(prev => [...prev, newPendingItem]);
        
        // Remove from modifiedItems if it was there
        if (modifiedItems[oldItemId]) {
        setModifiedItems(prev => {
            const newModified = { ...prev };
            delete newModified[oldItemId];
            return newModified;
        });
        }
        
        // Update the node
        setNodes(nds => nds.map(n => {
        if (n.id === oldNodeId) {
            return {
            ...n,
            id: newNodeId,
            data: {
                ...n.data,
                itemId: newPendingId,
                itemCode: updates.itemCode,
                itemName: updates.itemName !== undefined ? updates.itemName : n.data.itemName,
                description: updates.description !== undefined ? updates.description : n.data.description,
                itemType: updates.itemType !== undefined ? updates.itemType : n.data.itemType,
                isPhantom: updates.isPhantom !== undefined ? updates.isPhantom : n.data.isPhantom,
                isPending: true,
                isNew: true,
                isModified: false,
            },
            };
        }
        return n;
        }));
        
        // Update edges connected to this node
        setEdges(eds => eds.map(edge => {
        let updatedEdge = edge;
        
        if (edge.source === oldNodeId) {
            updatedEdge = {
            ...updatedEdge,
            id: `bom-${newNodeId}-${updatedEdge.target}`,
            source: newNodeId,
            data: {
                ...updatedEdge.data,
                bomId: undefined,
            },
            };
        }
        
        if (edge.target === oldNodeId) {
            updatedEdge = {
            ...updatedEdge,
            id: `bom-${updatedEdge.source}-${newNodeId}`,
            target: newNodeId,
            data: {
                ...updatedEdge.data,
                bomId: undefined,
            },
            };
        }
        
        return updatedEdge;
        }));
        
        // Update originalState - remove the old node and edges connected to it
        // This ensures only the new relationships are created, not existing sub-assembly ones
        setOriginalState(prev => ({
        nodes: prev.nodes.filter(n => n.data?.itemId !== oldItemId),
        edges: prev.edges.filter(e => {
            const sourceNode = prev.nodes.find(n => n.id === e.source);
            const targetNode = prev.nodes.find(n => n.id === e.target);
            // Keep edge if it doesn't involve the old item
            return sourceNode?.data?.itemId !== oldItemId && targetNode?.data?.itemId !== oldItemId;
        }),
        }));
        
        setSelectedNode(newNodeId);
        setHasUnsavedChanges(true);
        return;
    }
    
    // For pending items - update the pending item in the list
    if (node.data.isPending) {
        setPendingItems(prev => prev.map(item => {
        if (item.id === node.data.itemId) {
            return {
            ...item,
            item_code: updates.itemCode !== undefined ? updates.itemCode : item.item_code,
            name: updates.itemName !== undefined ? updates.itemName : item.name,
            description: updates.description !== undefined ? updates.description : item.description,
            item_type: updates.itemType !== undefined ? updates.itemType : item.item_type,
            is_phantom: updates.isPhantom !== undefined ? updates.isPhantom : item.is_phantom,
            };
        }
        return item;
        }));
    } else {
        // For existing items where code is NOT changed, track as modification
        setModifiedItems(prev => ({
        ...prev,
        [node.data.itemId]: {
            id: node.data.itemId,
            item_code: node.data.itemCode, // Keep original code
            name: updates.itemName !== undefined ? updates.itemName : node.data.itemName,
            description: updates.description !== undefined ? updates.description : (node.data.description || ''),
            item_type: updates.itemType !== undefined ? updates.itemType : node.data.itemType,
            is_phantom: updates.isPhantom !== undefined ? updates.isPhantom : node.data.isPhantom,
        },
        }));
    }
    
    // Update the node data
    setNodes(nds => nds.map(n => {
        if (n.id === nodeId) {
        return {
            ...n,
            data: {
            ...n.data,
            itemCode: updates.itemCode !== undefined ? updates.itemCode : n.data.itemCode,
            itemName: updates.itemName !== undefined ? updates.itemName : n.data.itemName,
            description: updates.description !== undefined ? updates.description : n.data.description,
            itemType: updates.itemType !== undefined ? updates.itemType : n.data.itemType,
            isPhantom: updates.isPhantom !== undefined ? updates.isPhantom : n.data.isPhantom,
            },
        };
        }
        return n;
    }));
    
    setHasUnsavedChanges(true);
  }, [nodes, modifiedItems, setNodes, setEdges]);

  // Handle deleting a node from the edit panel
  const handleDeleteNode = useCallback((nodeId) => {
    const nodeToDelete = nodes.find(n => n.id === nodeId);
    
    if (!nodeToDelete) return;
    
    if (nodeToDelete.data?.isPending) {
        setPendingItems(prev => prev.filter(p => p.id !== nodeToDelete.data.itemId));
    }
    
    // Remove from modifiedItems if it was modified
    if (modifiedItems[nodeToDelete.data?.itemId]) {
        setModifiedItems(prev => {
        const newModified = { ...prev };
        delete newModified[nodeToDelete.data.itemId];
        return newModified;
        });
    }
    
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setSelectedNode(null);
    setHasUnsavedChanges(true);
    setValidationErrors([]);
  }, [nodes, modifiedItems, setNodes, setEdges]);

  // Load existing BOM
  const handleLoadBOM = async (item) => {
    try {
      const { nodes: bomNodes, edges: bomEdges } = await loadBOMTree(item.id);
      
      const { nodes: layoutedNodes, edges: layoutedEdges } = layoutBOM(bomNodes, bomEdges);
      
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
      
      // Deep clone for original state to avoid reference issues
      setOriginalState({ 
        nodes: layoutedNodes.map(n => ({ 
          ...n, 
          data: { ...n.data }, 
          position: { ...n.position } 
        })), 
        edges: layoutedEdges.map(e => ({ 
          ...e, 
          data: { ...e.data } 
        })) 
      });
      
      setHasUnsavedChanges(false);
      setValidationErrors([]);
      setPendingItems([]);
      setModifiedItems({});
      setShowLoadModal(false);
    } catch (err) {
      console.error('Failed to load BOM:', err);
    }
  };

  // Validate before save
  const validateBeforeSave = useCallback(() => {
    const validation = validateBOMConnectivity(nodes, edges);
    
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return { isValid: false, validation };
    }
    
    setValidationErrors([]);
    return { isValid: true, validation };
  }, [nodes, edges]);

  // Open save confirmation modal
  const handleSaveClick = () => {
    const { isValid, validation } = validateBeforeSave();
    
    if (!isValid) {
      const errorMsg = [
        'Cannot save - please fix these issues:',
        '',
        ...validation.errors,
        '',
        validation.orphanNodes?.length > 0 
          ? `Disconnected items: ${validation.orphanNodes.map(n => n.itemCode).join(', ')}`
          : '',
      ].filter(Boolean).join('\n');
      
      alert(errorMsg);
      return;
    }

    setShowSaveModal(true);
  };

  // Confirm and execute save
  const handleConfirmSave = async () => {
    setShowSaveModal(false);
    setIsSaving(true);
    
    try {
      // Pass pending items and modified items to saveBOMChanges
      const modifiedItemsList = Object.values(modifiedItems);
      const results = await saveBOMChanges(diff, pendingItems, modifiedItemsList, user?.id);
      
      if (results.errors && results.errors.length > 0) {
        alert(`Save completed with errors:\n${results.errors.join('\n')}`);
      } else {
        const summary = [];
        if (results.itemsCreated > 0) summary.push(`Items created: ${results.itemsCreated}`);
        if (results.itemsUpdated > 0) summary.push(`Items updated: ${results.itemsUpdated}`);
        if (results.created > 0) summary.push(`BOM relationships created: ${results.created}`);
        if (results.updated > 0) summary.push(`Quantities updated: ${results.updated}`);
        if (results.deleted > 0) summary.push(`Relationships deleted: ${results.deleted}`);
        
        alert(`BOM saved successfully!\n\n${summary.join('\n')}`);
      }
      
      // Clear pending items and modified items
      setPendingItems([]);
      setModifiedItems({});
      
      // Update nodes to remove isPending flag and update IDs
      setNodes(nds => nds.map(n => {
        if (n.data.isPending && results.itemIdMap[n.data.itemId]) {
          return {
            ...n,
            data: {
              ...n.data,
              itemId: results.itemIdMap[n.data.itemId],
              isPending: false,
              isNew: false,
            },
          };
        }
        return { ...n, data: { ...n.data, isPending: false, isNew: false } };
      }));
      
      // Deep clone for original state after save
      setOriginalState({ 
        nodes: nodes.map(n => ({ 
          ...n, 
          data: { ...n.data }, 
          position: { ...n.position } 
        })), 
        edges: edges.map(e => ({ 
          ...e, 
          data: { ...e.data } 
        })) 
      });
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Failed to save BOM:', err);
      alert('Failed to save: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle "Save as New" - convert current BOM to a new product
  const handleSaveAsNew = useCallback((newTopLevelItem) => {
    setShowSaveAsNewModal(false);
    
    const currentTopLevelNode = topLevelItem;
    
    if (!currentTopLevelNode) {
        // No existing structure - just add new item
        setPendingItems(prev => [...prev, newTopLevelItem]);
        
        const newNode = {
        id: `node-${newTopLevelItem.id}`,
        type: 'bomItem',
        position: { x: 200, y: 200 },
        data: {
            itemId: newTopLevelItem.id,
            itemCode: newTopLevelItem.item_code,
            itemName: newTopLevelItem.name,
            itemType: newTopLevelItem.item_type,
            isPhantom: newTopLevelItem.is_phantom,
            isPending: true,
            isNew: true,
            description: newTopLevelItem.description || '',
        },
        };
        
        setNodes(nds => nds.concat(newNode));
        setHasUnsavedChanges(true);
        return;
    }
    
    const oldTopLevelId = currentTopLevelNode.id;
    const oldItemId = currentTopLevelNode.data.itemId;
    const newNodeId = `node-${newTopLevelItem.id}`;
    
    // Add new item to pending items
    setPendingItems(prev => [...prev, newTopLevelItem]);
    
    // Remove old top-level from modified items if it was there
    if (modifiedItems[oldItemId]) {
        setModifiedItems(prev => {
        const newModified = { ...prev };
        delete newModified[oldItemId];
        return newModified;
        });
    }
    
    // Update the top-level node
    setNodes(nds => nds.map(node => {
        if (node.id === oldTopLevelId) {
        return {
            ...node,
            id: newNodeId,
            data: {
            ...node.data,
            itemId: newTopLevelItem.id,
            itemCode: newTopLevelItem.item_code,
            itemName: newTopLevelItem.name,
            itemType: newTopLevelItem.item_type,
            isPhantom: newTopLevelItem.is_phantom,
            isPending: true,
            isNew: true,
            isModified: false,
            description: newTopLevelItem.description || '',
            },
        };
        }
        return node;
    }));
    
    // Update edges that pointed to the old top-level node
    setEdges(eds => eds.map(edge => {
        if (edge.target === oldTopLevelId) {
        return {
            ...edge,
            id: `bom-${edge.source}-${newNodeId}`,
            target: newNodeId,
            data: {
            ...edge.data,
            bomId: undefined, // Clear bomId so it's treated as new
            },
        };
        }
        return edge;
    }));
    
    // Update originalState - ONLY remove the old top-level node and edges TO it
    // Keep all sub-assembly edges so they don't get recreated
    setOriginalState(prev => ({
        nodes: prev.nodes.filter(n => n.data?.itemId !== oldItemId),
        edges: prev.edges.filter(e => {
        // Find the target node to check its itemId
        const targetNode = prev.nodes.find(n => n.id === e.target);
        // Keep edge if it doesn't target the old top-level
        return targetNode?.data?.itemId !== oldItemId;
        }),
    }));
    
    setHasUnsavedChanges(true);
    setValidationErrors([]);
  }, [topLevelItem, modifiedItems, setNodes, setEdges]);

  // Update edge quantity
  const handleQuantityChange = useCallback((edgeId, newQuantity) => {
    setEdges((eds) =>
      eds.map((edge) => {
        if (edge.id === edgeId) {
          return {
            ...edge,
            data: {
              ...edge.data,
              quantity: parseFloat(newQuantity) || 1,
            },
          };
        }
        return edge;
      })
    );
    setHasUnsavedChanges(true);
  }, [setEdges]);

  // Delete selected elements
  const handleDelete = useCallback(() => {
    if (selectedEdge) {
      setEdges((eds) => eds.filter((e) => e.id !== selectedEdge));
      setSelectedEdge(null);
      setHasUnsavedChanges(true);
      setValidationErrors([]);
    }
    if (selectedNode) {
      const nodeToDelete = nodes.find(n => n.id === selectedNode);
      
      if (nodeToDelete?.data?.isPending) {
        setPendingItems(prev => prev.filter(p => p.id !== nodeToDelete.data.itemId));
      }
      
      if (modifiedItems[nodeToDelete?.data?.itemId]) {
        setModifiedItems(prev => {
          const newModified = { ...prev };
          delete newModified[nodeToDelete.data.itemId];
          return newModified;
        });
      }
      
      setEdges((eds) => eds.filter((e) => e.source !== selectedNode && e.target !== selectedNode));
      setNodes((nds) => nds.filter((n) => n.id !== selectedNode));
      setSelectedNode(null);
      setHasUnsavedChanges(true);
      setValidationErrors([]);
    }
  }, [selectedNode, selectedEdge, nodes, modifiedItems, setNodes, setEdges]);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Delete' || event.key === 'Backspace') {
      handleDelete();
    }
    if (event.key === 'Escape') {
      setSelectedNode(null);
      setSelectedEdge(null);
    }
  }, [handleDelete]);

  // Context menu
  const onPaneContextMenu = useCallback((event) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Clear canvas
  const handleClear = useCallback(() => {
    if (nodes.length === 0) return;
    if (!window.confirm('Clear the canvas? All unsaved changes will be lost.')) return;
    
    setNodes([]);
    setEdges([]);
    setOriginalState({ nodes: [], edges: [] });
    setPendingItems([]);
    setModifiedItems({});
    setHasUnsavedChanges(false);
    setValidationErrors([]);
    setSelectedNode(null);
    setSelectedEdge(null);
  }, [nodes, setNodes, setEdges]);

  // Auto-layout
  const handleAutoLayout = useCallback(() => {
    if (nodes.length === 0) return;
    
    const { nodes: layoutedNodes, edges: layoutedEdges } = layoutBOM(nodes, edges);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [nodes, edges, setNodes, setEdges]);

  // Check if we can save
  const canSave = hasUnsavedChanges && !isSaving && nodes.length > 0;

  return (
    <div className="bom-configurator" onKeyDown={handleKeyDown} tabIndex={0}>
      {/* Header */}
      <div className="bom-configurator-header">
        <div className="header-left">
          <h1>BOM Configurator</h1>
          {topLevelItem && (
            <span className={`top-level-badge ${topLevelItem.data?.isPending ? 'is-pending' : ''}`}>
              Building: {topLevelItem.data?.itemCode}
              {topLevelItem.data?.isPending && <span className="pending-suffix"> (NEW)</span>}
            </span>
          )}
          {topLevelItems.length > 1 && (
            <span className="warning-badge" title="Multiple top-level items - connect them to save">
              {topLevelItems.length} top-level items
            </span>
          )}
          {hasUnsavedChanges && <span className="unsaved-badge">Unsaved Changes</span>}
          {modifiedParents.length > 1 && (
            <span className="multi-bom-badge" title="Changes affect multiple BOMs">
              {modifiedParents.length} BOMs modified
            </span>
          )}
          {pendingItems.length > 0 && (
            <span className="pending-items-badge" title="New items to be created">
              {pendingItems.length} new item(s)
            </span>
          )}
          {modifiedItemsCount > 0 && (
            <span className="modified-items-badge" title="Existing items with changes">
              {modifiedItemsCount} item(s) edited
            </span>
          )}
        </div>
        <div className="header-actions">
          <button 
            className="btn-secondary"
            onClick={handleAutoLayout}
            disabled={nodes.length === 0}
          >
            Auto Layout
          </button>
          <button 
            className="btn-secondary"
            onClick={handleClear}
            disabled={nodes.length === 0}
          >
            Clear
          </button>

          <button 
                className="btn-secondary"
                onClick={() => setShowSaveAsNewModal(true)}
                disabled={nodes.length === 0 || isSaving}
                title="Create a new product based on current BOM structure"
            >
                Save as New
          </button>


          <button 
            className="btn-primary"
            onClick={handleSaveClick}
            disabled={!canSave}
            title={nodes.length === 0 ? 'Add items to canvas first' : ''}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Validation Errors Banner */}
      {validationErrors.length > 0 && (
        <div className="validation-errors-banner">
          <strong>Validation Issues:</strong>
          <ul>
            {validationErrors.map((err, idx) => (
              <li key={idx}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Modified BOMs Summary Bar */}
      {modifiedParents.length > 0 && (
        <div className="modified-boms-bar">
          <span className="modified-label">Modified BOMs:</span>
          <div className="modified-tags">
            {modifiedParents.map(parent => (
              <span 
                key={parent.parentId} 
                className={`modified-tag ${parent.isTargetItem ? 'is-target' : 'is-sublevel'}`}
                title={`${parent.created} new, ${parent.updated} updated, ${parent.deleted} deleted`}
              >
                {parent.parentCode}
                <span className="change-count">{parent.changeCount}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="bom-configurator-body">
        {/* Left Panel - Item Search */}
        <ItemSearchPanel 
          onLoadBOM={() => setShowLoadModal(true)}
          onCreateItem={() => setShowCreateModal(true)}
          pendingItemCodes={pendingItemCodes}
          topLevelItem={topLevelItem?.data}
        />

        {/* Main Canvas */}
        <div className="bom-canvas-wrapper" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodesWithStatus}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={(_, node) => {
              setSelectedNode(node.id);
              setSelectedEdge(null);
            }}
            onEdgeClick={(_, edge) => {
              setSelectedEdge(edge.id);
              setSelectedNode(null);
            }}
            onPaneClick={() => {
              setSelectedNode(null);
              setSelectedEdge(null);
              closeContextMenu();
            }}
            onPaneContextMenu={onPaneContextMenu}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            snapToGrid
            snapGrid={[15, 15]}
            defaultEdgeOptions={{
              type: 'bomEdge',
              animated: false,
            }}
            connectionLineStyle={{ stroke: '#64748b', strokeWidth: 2 }}
            deleteKeyCode={null}
          >
            <Background variant="dots" gap={20} size={1} color="#334155" />
            <Controls position="bottom-right" />
            <MiniMap 
              position="bottom-left"
              nodeColor={(node) => {
                if (node.data?.isTopLevel) return '#22c55e';
                if (node.data?.isPending) return '#8b5cf6';
                if (node.data?.isModified) return '#60a5fa';
                if (node.data?.hasModifiedBOM) return '#f59e0b';
                if (node.data?.isPhantom) return '#f59e0b';
                if (node.data?.hasChildren) return '#3b82f6';
                return '#64748b';
              }}
              maskColor="rgba(15, 23, 42, 0.8)"
            />
            
            {/* Instructions Panel */}
            <Panel position="top-right" className="instructions-panel">
              <div className="instructions">
                <p>How to use:</p>
                <ul>
                  <li><strong>1.</strong> Click "Load BOM" to edit existing</li>
                  <li><strong>2.</strong> Or click "+ New" to create items</li>
                  <li><strong>3.</strong> Drag items from left panel</li>
                  <li><strong>4.</strong> Connect: component (right) to parent (left)</li>
                  <li><strong>5.</strong> Click edge to edit quantity</li>
                  <li><strong>6.</strong> All items must be connected to save</li>
                </ul>
                <p className="warning-note">
                  Editing sub-assemblies will update their BOMs too!
                </p>
                <div className="legend-mini">
                  <span className="legend-item"><span className="dot" style={{background:'#22c55e'}}></span> Top-level</span>
                  <span className="legend-item"><span className="dot" style={{background:'#3b82f6'}}></span> Sub-assembly</span>
                  <span className="legend-item"><span className="dot" style={{background:'#64748b'}}></span> Raw material</span>
                  <span className="legend-item"><span className="dot" style={{background:'#8b5cf6'}}></span> New (unsaved)</span>
                  <span className="legend-item"><span className="dot" style={{background:'#60a5fa'}}></span> Modified</span>
                </div>
              </div>
            </Panel>

            {/* Empty Canvas Message */}
            {nodes.length === 0 && (
              <Panel position="top-center" className="no-target-panel">
                <div className="no-target-message">
                  <p>Canvas is empty</p>
                  <p>Click <strong>"Load BOM"</strong> to edit an existing BOM, or click <strong>"+ New"</strong> to create a new item.</p>
                </div>
              </Panel>
            )}
          </ReactFlow>

          {/* Context Menu */}
          {contextMenu && (
            <div 
                className="context-menu"
                style={{ left: contextMenu.x, top: contextMenu.y }}
            >
                <button onClick={() => { setShowLoadModal(true); closeContextMenu(); }}>
                Load Existing BOM
                </button>
                <button onClick={() => { setShowCreateModal(true); closeContextMenu(); }}>
                Create New Item
                </button>
                <button 
                onClick={() => { setShowSaveAsNewModal(true); closeContextMenu(); }}
                disabled={nodes.length === 0}
                >
                Save as New Product
                </button>
                <button onClick={() => { handleAutoLayout(); closeContextMenu(); }}>
                Auto Layout
                </button>
                <button onClick={() => { handleClear(); closeContextMenu(); }}>
                Clear Canvas
                </button>
            </div>
          )}
        </div>

        {/* Right Panel - Edit Item (when node selected) */}
        {selectedNode && (
          <EditItemPanel
            node={selectedNodeObject}
            onUpdateNode={handleUpdateNode}
            onDeleteNode={handleDeleteNode}
            onClose={() => setSelectedNode(null)}
            isTopLevel={selectedNodeObject?.data?.isTopLevel || false}
          />
        )}

        {/* Right Panel - Edge Details (when edge selected) */}
        {selectedEdge && !selectedNode && (
          <div className="edge-details-panel">
            <h3>Connection Details</h3>
            {(() => {
              const edge = edges.find(e => e.id === selectedEdge);
              const sourceNode = nodesWithStatus.find(n => n.id === edge?.source);
              const targetNode = nodesWithStatus.find(n => n.id === edge?.target);
              
              if (!edge || !sourceNode || !targetNode) return null;

              const isSubLevelEdit = !targetNode.data?.isTopLevel && targetNode.data?.hasChildren;
              
              return (
                <>
                  {isSubLevelEdit && (
                    <div className="sublevel-warning">
                      This edits <strong>{targetNode.data.itemCode}</strong>'s BOM (a sub-assembly)
                    </div>
                  )}
                  <div className="detail-row">
                    <label>Child (Component):</label>
                    <span>
                      {sourceNode.data.itemCode}
                      {sourceNode.data.isPending && <span className="node-type-tag pending">NEW</span>}
                      {sourceNode.data.isModified && <span className="node-type-tag modified">EDITED</span>}
                      {!sourceNode.data.isPending && !sourceNode.data.isModified && sourceNode.data.hasChildren ? (
                        <span className="node-type-tag subasm">SUB-ASM</span>
                      ) : !sourceNode.data.isPending && !sourceNode.data.isModified && (
                        <span className="node-type-tag rawmat">RAW</span>
                      )}
                    </span>
                  </div>
                  <div className="detail-row">
                    <label>Parent (Assembly):</label>
                    <span>
                      {targetNode.data.itemCode}
                      {targetNode.data.isTopLevel && <span className="top-level-indicator"> (TOP-LEVEL)</span>}
                      {targetNode.data.isPending && <span className="node-type-tag pending">NEW</span>}
                      {targetNode.data.isModified && <span className="node-type-tag modified">EDITED</span>}
                    </span>
                  </div>
                  <div className="detail-row">
                    <label>Quantity Required:</label>
                    <input
                      type="number"
                      step="0.000001"
                      min="0"
                      value={edge.data?.quantity || 1}
                      onChange={(e) => handleQuantityChange(edge.id, e.target.value)}
                    />
                  </div>
                  <div className="detail-row">
                    <label>Phantom Pass-through:</label>
                    <span>{sourceNode.data.isPhantom ? 'Yes' : 'No'}</span>
                  </div>
                  <button 
                    className="btn-danger"
                    onClick={handleDelete}
                  >
                    Delete Connection
                  </button>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Load BOM Modal */}
      {showLoadModal && (
        <LoadBOMModal
          onClose={() => setShowLoadModal(false)}
          onSelect={handleLoadBOM}
        />
      )}

      {/* Create Item Modal */}
      {showCreateModal && (
        <CreateItemModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateItem}
          existingPendingCodes={pendingItemCodes}
        />
      )}

      {/* Save Confirmation Modal */}
      {showSaveModal && (
        <SaveConfirmModal
          topLevelItem={topLevelItem?.data}
          modifiedParents={modifiedParents}
          diff={diff}
          pendingItems={pendingItems}
          modifiedItems={Object.values(modifiedItems)}
          onConfirm={handleConfirmSave}
          onCancel={() => setShowSaveModal(false)}
        />
      )}

      {/* Save As New Modal */}
      {showSaveAsNewModal && (
        <SaveAsNewModal
            onClose={() => setShowSaveAsNewModal(false)}
            onConfirm={handleSaveAsNew}
            currentTopLevel={topLevelItem?.data}
            existingPendingCodes={pendingItemCodes}
        />
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading BOM structure...</p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="error-toast">
          {error}
        </div>
      )}
    </div>
  );
};

export default BOMConfigurator;